import { useId, type FormHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from '../internal/cx';
import styles from './Form.module.css';

export interface FormSectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  titleAs?: 'h2' | 'h3' | 'h4';
  /** Field grid columns on wide screens (always 1 column on narrow screens). */
  columns?: 1 | 2 | 3;
}

/** A titled group of fields. Renders a <section> labelled by its heading. */
export function FormSection({
  title,
  description,
  titleAs: Heading = 'h2',
  columns = 1,
  className,
  children,
  ...rest
}: FormSectionProps) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className={cx(styles.section, className)} {...rest}>
      <div className={styles.sectionHeader}>
        <Heading id={headingId} className={styles.sectionTitle}>
          {title}
        </Heading>
        {description && <p className={styles.sectionDescription}>{description}</p>}
      </div>
      <div className={cx(styles.sectionGrid, styles[`cols-${columns}`])}>{children}</div>
    </section>
  );
}

export interface FormActionsProps extends HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'end' | 'between';
  /** Stick to the bottom of the scroll container (long forms). */
  sticky?: boolean;
}

/** Button row for forms. Put the primary action last (visually rightmost). */
export function FormActions({
  align = 'end',
  sticky = false,
  className,
  ...rest
}: FormActionsProps) {
  return (
    <div
      className={cx(
        styles.actions,
        styles[`actions-${align}`],
        sticky && styles.actionsSticky,
        className,
      )}
      {...rest}
    />
  );
}

export type FormProps = FormHTMLAttributes<HTMLFormElement>;

/** A <form> with consistent vertical rhythm. `noValidate` by default (we show our own errors). */
export function Form({ className, noValidate = true, ...rest }: FormProps) {
  return <form className={cx(styles.form, className)} noValidate={noValidate} {...rest} />;
}
